import React, { useCallback, useState } from "react";

import { PickerTimeProps } from "antd/es/date-picker/generatePicker";
import { MenuProps } from "antd/es/menu";
import cn from "classnames";
import { isEqual, isValid, parse } from "date-fns";

import Dropdown from "../../Dropdown";
import useDatePicker from "../util/useDatePicker";

import { block } from "./consts";
import useTimeMenu, { isTimeDisabled } from "./useTimeMenu";

export interface TimeDropdownPickerProps
  extends Pick<
    PickerTimeProps<Date>,
    "allowClear" | "disabled" | "disabledTime" | "placeholder" | "size" | "suffixIcon" | "value"
  > {
  className?: string;
  menu?: MenuProps;
  onChange?: (value: Date | null) => void;
  stepMinutes?: number;
  timeFormat: string;
  use12Hours: boolean;
}

const TimeDropdownPicker = ({
  allowClear = false,
  className,
  disabled,
  disabledTime,
  menu: menuOverride,
  onChange,
  placeholder,
  size = "middle",
  stepMinutes = 15,
  suffixIcon = null,
  timeFormat,
  use12Hours,
  value,
}: TimeDropdownPickerProps) => {
  const DatePickerComponent = useDatePicker();
  const [open, setOpen] = useState(false);

  const handleMenuSelect = useCallback(
    (date: Date) => {
      onChange?.(date);
      setOpen(false);
    },
    [onChange]
  );

  const defaultMenu = useTimeMenu(stepMinutes, handleMenuSelect, value, timeFormat, disabledTime);
  const menu = menuOverride ?? defaultMenu;

  const handlePickerChange = useCallback(
    (date: Date | null) => {
      onChange?.(date);
      setOpen(false);
    },
    [onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const text = event.target.value;
      if (!text) {
        return;
      }
      const parsed = parse(text, timeFormat, value ?? new Date());
      if (value && isEqual(parsed, value)) {
        return;
      }
      if (isValid(parsed) && !isTimeDisabled(parsed, disabledTime)) {
        setTimeout(() => handleMenuSelect(parsed));
      }
    },
    [disabledTime, handleMenuSelect, timeFormat, value]
  );

  const handleMenuContainerMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const handleOpenChange = useCallback((visible: boolean) => {
    setOpen(visible);
  }, []);

  return (
    <Dropdown
      disabled={disabled}
      dropdownRender={(menuNode) => (
        <div onMouseDown={handleMenuContainerMouseDown}>{menuNode}</div>
      )}
      forceRender
      menu={menu}
      onOpenChange={handleOpenChange}
      open={open}
      placement="bottom"
      trigger={["click"]}
    >
      <DatePickerComponent
        allowClear={allowClear}
        className={cn(block(), className)}
        disabled={disabled}
        disabledTime={disabledTime}
        dropdownClassName={block("overlay")}
        format={timeFormat}
        mode={undefined}
        onBlur={handleBlur}
        onChange={handlePickerChange}
        open
        picker="time"
        placeholder={placeholder}
        showSecond={false}
        size={size}
        suffixIcon={suffixIcon}
        use12Hours={use12Hours}
        value={value}
      />
    </Dropdown>
  );
};

TimeDropdownPicker.displayName = "TimeDropdownPicker";

export default TimeDropdownPicker;
